// src/components/FoodMenuManagement.jsx
import React, { useEffect, useState } from "react";
import config from "../config";
import "../css/theme.css";
import "../css/components.css";
import "./FoodMenuManagement.css";
import ToastContainer, { useToast } from "./common/Toast";

const emptyRow = () => ({ id: null, name: "", price: "", active: true });

// Sheet-style inline table (one row per menu item, directly editable),
// same batch-entry pattern as RecordDailyEntryForm.jsx - not a popup modal
// per item, which took too many clicks for adding several items at once.
const FoodMenuManagement = () => {
  const { toasts, showToast, dismissToast } = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const fetchMenu = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${config.BASE_URL}/api/food-menu`, { headers: config.getHeaders() });
      if (!res.ok) throw new Error("Failed to fetch menu");
      const data = await res.json();
      setRows(data.length > 0 ? data.map((item) => ({ ...item, price: String(item.price) })) : [emptyRow()]);
    } catch (err) {
      console.error(err);
      setRows([emptyRow()]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMenu();
  }, []);

  const updateRow = (index, changes) => {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...changes } : r)));
  };

  const addRow = () => setRows((prev) => [...prev, emptyRow()]);

  // Only ever removes an unsaved draft row - an existing (saved) item has
  // no remove button here, see the table below. Deactivating a saved item
  // is done via its own Active checkbox, not by removing its row.
  const removeDraftRow = (index) => setRows((prev) => prev.filter((_, i) => i !== index));

  const handleSave = async () => {
    if (isSaving) return;

    const rowsToSave = rows.filter((r) => r.name.trim() !== "" || r.price !== "");
    for (const row of rowsToSave) {
      if (!row.name.trim()) {
        showToast("Every item needs a name.", "warning");
        return;
      }
      const price = parseFloat(row.price);
      if (!price || price <= 0) {
        showToast(`Enter a valid price for "${row.name}".`, "warning");
        return;
      }
    }
    if (rowsToSave.length === 0) {
      showToast("Add at least one item.", "warning");
      return;
    }

    setIsSaving(true);
    try {
      for (const row of rowsToSave) {
        const url = row.id
          ? `${config.BASE_URL}/api/food-menu/${row.id}`
          : `${config.BASE_URL}/api/food-menu`;
        const res = await fetch(url, {
          method: row.id ? "PUT" : "POST",
          headers: config.getHeaders(),
          body: JSON.stringify({ name: row.name.trim(), price: parseFloat(row.price), active: row.active }),
        });
        if (!res.ok) {
          const errBody = await res.json().catch(() => null);
          throw new Error(errBody?.error || `Failed to save "${row.name}"`);
        }
      }

      showToast("Menu saved.", "success");
      fetchMenu();
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to save menu. Please try again.", "danger");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="food-menu-management">
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      <div className="vt-page-header">
        <h2>Food Menu</h2>
      </div>

      {loading && <div className="loading-overlay">Loading...</div>}

      <div className="food-menu-sheet-wrapper">
        <table className="food-menu-sheet">
          <thead>
            <tr>
              <th className="row-number">#</th>
              <th>Item Name</th>
              <th>Price (₹)</th>
              <th>Active</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={row.id ?? `new-${index}`}>
                <td className="row-number">{index + 1}</td>
                <td>
                  <input
                    type="text"
                    value={row.name}
                    onChange={(e) => updateRow(index, { name: e.target.value })}
                    placeholder="Item name"
                  />
                </td>
                <td>
                  <input
                    type="number"
                    value={row.price}
                    onChange={(e) => updateRow(index, { price: e.target.value })}
                    placeholder="0"
                  />
                </td>
                <td className="food-menu-sheet__active-cell">
                  <input
                    type="checkbox"
                    checked={row.active}
                    onChange={(e) => updateRow(index, { active: e.target.checked })}
                  />
                </td>
                <td className="row-actions">
                  {!row.id && rows.length > 1 && (
                    <button
                      type="button"
                      className="remove-btn"
                      title="Remove row"
                      onClick={() => removeDraftRow(index)}
                    >
                      ✕
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <button type="button" className="add-btn" onClick={addRow}>
        + Add Item
      </button>

      <button type="button" className="submit-btn" onClick={handleSave} disabled={isSaving}>
        {isSaving ? "Saving..." : "Save Menu"}
      </button>
    </div>
  );
};

export default FoodMenuManagement;
